import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck } from "lucide-react";

const Field = ({ label, value, mono = true }) => (
    <div className="py-3 border-b hairline last:border-b-0 grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-4">
            <span className="mono-label">{label}</span>
        </div>
        <div className="col-span-12 sm:col-span-8">
            <span
                className={mono ? "mono-data" : ""}
                style={{
                    fontSize: mono ? 12.5 : 14.5,
                    fontFamily: mono
                        ? "JetBrains Mono, monospace"
                        : "IBM Plex Sans, sans-serif",
                    color: "var(--ink)",
                }}
            >
                {value || "—"}
            </span>
        </div>
    </div>
);

export const DisclosureReceiptPanel = ({ response }) => {
    if (!response) return null;

    if (!response.ok || !response.receipt) {
        return (
            <section
                className="panel"
                data-testid="disclosure-receipt-unconfigured"
            >
                <div className="px-6 sm:px-8 py-5 border-b hairline">
                    <p className="mono-label">
                        Governed disclosure receipt
                    </p>
                    <h3
                        className="font-serif mt-1"
                        style={{ fontSize: 22, letterSpacing: "-0.01em" }}
                    >
                        No signed receipt produced
                    </h3>
                </div>
                <div className="px-6 sm:px-8 py-5">
                    <p style={{ color: "var(--ink-soft)" }}>
                        KATLAS is currently{" "}
                        <strong className="font-mono">
                            {response.katlas?.status}
                        </strong>
                        . The governed disclosure action was recorded as an
                        honest unconfigured response; no simulated receipt is
                        produced.
                    </p>
                    <pre
                        className="mt-4 panel-alt px-4 py-3 mono-data overflow-x-auto"
                        style={{ fontSize: 12 }}
                    >
                        {JSON.stringify(response.katlas, null, 2)}
                    </pre>
                </div>
            </section>
        );
    }

    const r = response.receipt;

    return (
        <section
            className="bg-white"
            style={{ border: "4px double var(--ink)" }}
            data-testid="disclosure-receipt-panel"
        >
            <div className="px-6 sm:px-10 py-6 border-b hairline flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <ShieldCheck size={18} strokeWidth={1.5} />
                    <div>
                        <p className="mono-label">
                            KATLAS · disclosure attestation
                        </p>
                        <h3
                            className="font-serif mt-1"
                            style={{
                                fontSize: 22,
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Sensitive disclosure route —{" "}
                            <span style={{ color: "var(--forest)" }}>
                                recorded
                            </span>
                        </h3>
                    </div>
                </div>
                <span
                    className="mono-label border border-[var(--forest)] px-2 py-1"
                    style={{ color: "var(--forest)" }}
                >
                    {r.verification || "—"}
                </span>
            </div>

            <div className="px-6 sm:px-10 py-6">
                <Field label="Receipt hash" value={r.receipt_hash} />
                <Field label="Signal category" value={r.signal_category} />
                <Field
                    label="Disclosure lock applied"
                    value={String(r.disclosure_lock_applied)}
                />
                <Field
                    label="Authorised clinician notified"
                    value={String(r.authorised_clinician_notified)}
                />
                <Field
                    label="Patient-facing message type"
                    value={r.patient_facing_message_type}
                />
                <Field
                    label="Clinician route choice"
                    value={r.clinician_route_choice}
                />
                <Field
                    label="Nominated carer involvement"
                    value={r.nominated_carer_involvement_status}
                />
                <Field
                    label="Acting role"
                    value={r.acting_role?.label}
                    mono={false}
                />
                <Field
                    label="Shared evidence refs"
                    value={(r.shared_evidence_refs || [])
                        .map((e) => e.ref)
                        .join("  ·  ")}
                />
                <Field
                    label="Withheld evidence count"
                    value={String(r.withheld_evidence_count)}
                />
                <Field label="Node timestamp" value={r.node_timestamp_utc} />
            </div>

            <div className="px-6 sm:px-10 py-5 border-t hairline panel-alt">
                <p
                    className="font-serif"
                    style={{
                        fontSize: 14.5,
                        color: "var(--ink)",
                        lineHeight: 1.55,
                    }}
                >
                    {r.data_boundary_statement}
                </p>
                <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                    <span className="mono-label">
                        Custody · Authority · Receipts
                    </span>
                    <Link
                        to={`/r/${encodeURIComponent(r.receipt_hash)}`}
                        className="inline-flex items-center gap-2 mono-label"
                        style={{ color: "var(--ink)" }}
                        data-testid="disclosure-public-receipt-link"
                    >
                        Public-safe receipt
                        <ExternalLink size={12} strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default DisclosureReceiptPanel;
